// Bénévolat (D-026, D-034, D-038) : les besoins des matchs à domicile (créés tout
// seuls), des événements du club et des appels à bénévoles lancés par le bureau.
//  - accueil de chaque membre : ma permanence, ou « le club a besoin de toi » ;
//  - page Bénévolat des membres : toutes les occasions à venir, « Je prends » ;
//  - onglet Bénévoles du bureau : demander des bénévoles (à la place d'un Framadate),
//    suivre qui vient, relancer ;
//  - casquette Bénévole (D-097) : sa pastille et la ligne « Le club a besoin de toi » des
//    accueils des autres casquettes, d'après les places à prendre des 7 jours ;
//  - mineurs (D-103) : seulement les créneaux que le bureau ouvre aux jeunes (« Jeunes ✓ » sur le tableau
//    du bureau, « Ouvert aux jeunes » dans un appel), jamais de groupe ; la base trie et refuse le reste ;
//  - lot 1 du pilote (D-103) : les créneaux en conflit avec ce qui m'occupe (mes matchs, mes séances, ma voiture,
//    mes autres créneaux) sortent des propositions et « Je prends » prévient d'abord ; « Mes créneaux » en tête de
//    la page ; un seul chiffre partout : les places encore libres des 7 jours ;
//  - besoins des matchs par équipe (D-119, ./besoins.ts) : réglés dans Club › Bénévolat (D-127) ; sur la carte d'un match,
//    « Modifier les besoins », « Inscrire quelqu'un » sur un besoin ouvert, un prénom à toucher pour le retirer avec un merci ;
//  - D-127 : l'onglet du bureau garde le quotidien (Créneaux · Groupes · Badges) et mène aux réglages (« Réglages du
//    bénévolat », Club › Bénévolat : src/ui/reglages.ts).
import './benevolat.css';
import {
  clubVolunteerNeeds, deleteVolunteerCall, leaveSlot, mySlotConflicts, saveVolunteerCall, setVolunteerCallClosed,
  takeSlot, volunteerCalls, type SlotConflict, type VolunteerCall, type VolunteerNeed,
} from '../lib/api';
import { wireNeedsTools } from './besoins';
import { addDays, localDate, parseLocalDate } from '../domain/schedule';
import {
  callProblem, conflictText, gapLine, holes, nextDay, nextSlot, occasions, openForMe, placesLabel, SOURCE_LABEL, withoutConflicts, type CallSlotDraft, type Occasion,
} from '../domain/volunteer';
import { firstName, isMinorAccount, volunteerApart, type Ctx } from './context';
import { setCallYouth, setSlotYouth, slotYouth } from '../lib/minors-legal';
import './minors.css';
import './reglages.css';
import { tv, vouvoie } from './vous';
import { renderBadgesBoard, renderBadgesHome } from './badges';
import { time } from './format';
import { $, $$, html, mount, openSheet, toast, type Html } from './dom';
import { emptyGuide } from '../guide/empty';
import { openCompose, type Draft } from './announcements';
import { renderBureauVolunteerSections, renderMyGroupsCard, slotGroupButton, wireSlotGroups } from './volunteer-groups';
import { clubVolunteerGroups, slotGroups, type VolunteerGroup } from '../lib/api';
import { decoratePins } from './groupes';

const NAME_KEY = 'arceau.benevole.prenom';
const savedName = () => { try { return localStorage.getItem(NAME_KEY) ?? ''; } catch { return ''; } };
const saveName = (n: string) => { try { localStorage.setItem(NAME_KEY, n); } catch { /* navigation privée */ } };
const knownName = (ctx: Ctx) => firstName(ctx) || savedName();

/** Horizon des besoins : la carte d'accueil voit 2 mois, les pages 4 mois (un tournoi se prépare tôt). */
const CARD_DAYS = 60, PAGE_DAYS = 120;

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
const day = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
const dayShort = (d: string) => parseLocalDate(d).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' });
const hour = (iso: string) => time(new Date(iso));
const toHHMM = (iso: string | null) => (iso ? new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '');

/** L'heure d'un besoin : celle du match ou de l'événement ; pour un appel, celle du créneau. */
export function needWhen(n: VolunteerNeed): string {
  if (!n.starts_at) return 'dans la journée';
  if (n.source === 'call') return n.ends_at ? `${hour(n.starts_at)} – ${hour(n.ends_at)}` : `dès ${hour(n.starts_at)}`;
  return hour(n.starts_at);
}

/** « Samedi 14 décembre · 14h30 » ou, pour un appel sur plusieurs jours, « Du sam. 13 déc. au dim. 14 déc. ». */
function occasionWhen(o: Occasion<VolunteerNeed>): string {
  const f = o.first;
  if (o.source !== 'call') return `${cap(day(f.occurrence_date))}${f.starts_at ? ` · ${hour(f.starts_at)}` : ''}`;
  return o.days.length > 1 ? `Du ${dayShort(o.days[0]!.date)} au ${dayShort(o.days[o.days.length - 1]!.date)}` : cap(day(f.occurrence_date));
}

const leftLabel = (n: number) => (n ? `${n} ${n > 1 ? 'places libres' : 'place libre'}` : 'Complet');

// -----------------------------------------------------------------------------
// Ce que je peux prendre (D-097, D-103) : ni complet, ni à moi, ni réservé à un autre groupe, ni en conflit
// -----------------------------------------------------------------------------
export interface MySlots {
  needs: VolunteerNeed[];
  /** Les créneaux que je peux prendre, sans ceux qui tombent pendant ce qui m'occupe déjà. */
  open: VolunteerNeed[];
  conflicts: Map<string, SlotConflict>;
  reserved: Map<string, string>;
  groups: VolunteerGroup[];
}

/** Les besoins des `days` prochains jours et ce que j'en peux prendre. */
export async function slotsForMe(ctx: Ctx, days: number): Promise<MySlots> {
  const club = ctx.club?.id;
  const none: MySlots = { needs: [], open: [], conflicts: new Map(), reserved: new Map(), groups: [] };
  if (!club || club.startsWith('host:')) return none;
  const today = localDate(new Date()), to = addDays(today, days);
  const [needs, groups, reserved, conflicts] = await Promise.all([
    clubVolunteerNeeds(club, today, to),
    clubVolunteerGroups(club).catch(() => [] as VolunteerGroup[]),
    slotGroups(club).catch(() => new Map<string, string>()),
    mySlotConflicts(club, today, to).catch(() => new Map<string, SlotConflict>()),
  ]);
  const open = withoutConflicts(openForMe(needs, reserved, new Set(groups.filter(g => g.is_member).map(g => g.id))), conflicts);
  return { needs, open, conflicts, reserved, groups };
}

let gapCache: { key: string; at: number; p: Promise<VolunteerNeed[]> } | null = null;

/** Les créneaux des 7 prochains jours que je peux prendre (relus au plus toutes les 30 s, et après chaque inscription). */
export function weekGaps(ctx: Ctx): Promise<VolunteerNeed[]> {
  const club = ctx.club?.id;
  if (!club || club.startsWith('host:')) return Promise.resolve([]);
  const today = localDate(new Date());
  const key = `${club}|${ctx.uid}|${today}`;
  if (!gapCache || gapCache.key !== key || Date.now() - gapCache.at > 30_000) {
    gapCache = { key, at: Date.now(), p: slotsForMe(ctx, 7).then(m => m.open).catch(() => [] as VolunteerNeed[]) };
  }
  return gapCache.p;
}

/** Le chiffre de la pastille, de la tuile et de la ligne (D-103) : les places que je peux prendre dans les 7 jours. */
export const weekPlaces = async (ctx: Ctx): Promise<number> => holes(await weekGaps(ctx));

/** « Je prends » sur un créneau en conflit (D-103) : la fiche d'avertissement, « Je prends quand même » ou « Annuler ».
 *  Sans conflit : oui tout de suite. */
export function confirmTake(ctx: Ctx, n: { label: string; title: string } | undefined, c: SlotConflict | undefined): Promise<boolean> {
  if (!c) return Promise.resolve(true);
  const t = conflictText(c, vouvoie(ctx));
  return new Promise(resolve => {
    let yes = false;
    openSheet(n ? `${n.label} · ${n.title}` : 'Ce créneau', html`
      <div class="cf-sheet" data-guide="vol-conflict">
        <p class="cf-lead"><span aria-hidden="true">⚠️</span> <strong>${t.lead}</strong> : ${t.rest}</p>
        <p class="muted small">${tv(ctx, 'Le créneau tombe pendant ce qui t’occupe déjà. Prends-le seulement si tu peux faire les deux.',
          'Le créneau tombe pendant ce qui vous occupe déjà. Prenez-le seulement si vous pouvez faire les deux.')}</p>
        <div class="btn-row cf-actions">
          <button type="button" class="btn btn-primary" id="cf-no">Annuler</button>
          <button type="button" class="btn btn-ghost" id="cf-yes">Je prends quand même</button>
        </div>
      </div>`, (sheet, close) => {
      $(sheet, '#cf-no')!.addEventListener('click', () => close());
      $(sheet, '#cf-yes')!.addEventListener('click', () => { yes = true; close(); });
    }, () => resolve(yes));
  });
}

/** La ligne sous un créneau de la liste du club : ce qui m'occupe à son heure. */
const conflictNote = (ctx: Ctx, c: SlotConflict | undefined): Html | string =>
  c ? html`<span class="cf-note"><span aria-hidden="true">⚠️</span> ${conflictText(c, vouvoie(ctx)).lead}</span>` : '';

/** Un créneau pris ou rendu : la pastille de la casquette Bénévole se met à jour (app.ts l'écoute). */
export function slotsChanged(): void {
  gapCache = null;
  window.dispatchEvent(new Event('arceau:creneaux'));
}

/** La ligne « Le club a besoin de toi » des accueils hors casquette Bénévole : une seule, et seulement
 *  si un créneau manque dans les 7 jours ; elle mène à la casquette Bénévole (data-go-hat, app.ts). */
export async function renderVolunteerLine(el: HTMLElement, ctx: Ctx, opts: { vous?: boolean } = {}): Promise<void> {
  if (!volunteerApart(ctx)) return;
  const line = gapLine(await weekGaps(ctx), localDate(new Date()), opts.vous);
  if (!line) { mount(el, html``); return; }
  mount(el, html`
    <a class="bn-line" href="#/" data-go-hat="benevole" data-guide="home-bn">
      <span class="bn-ic" aria-hidden="true">❤️</span>
      <span class="bn-txt"><b>${line.lead}</b> · ${line.rest}</span>
      <span class="bn-go">Bénévole ›</span>
    </a>`);
}

/** Carte d'accueil : ma prochaine permanence, sinon les créneaux libres (sans ceux en conflit, D-103). */
export async function renderVolunteerCard(el: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club) return;   // mineurs compris (D-041) : les jeunes tiennent la table, arbitrent les petits
  const { needs, open } = await slotsForMe(ctx, CARD_DAYS).catch(() => ({ needs: [] as VolunteerNeed[], open: [] as VolunteerNeed[] }));
  const mine = needs.filter(n => n.mine);
  const reload = () => renderVolunteerCard(el, ctx);

  if (!mine.length && !open.length) { mount(el, html``); return; }
  const firstOpen = occasions(open)[0];
  const capEl = () => void renderBadgesHome($(el, '#vol-cap')!, ctx);   // le prochain cap (D-100), comme sur l'accueil Bénévole
  // Les autres bénévoles du créneau (mon prénom retiré une fois).
  const myName = knownName(ctx);
  const others = (n: VolunteerNeed) => { const i = n.names.indexOf(myName); return i < 0 ? n.names.slice(1) : n.names.filter((_, j) => j !== i); };
  const slots = firstOpen?.days.flatMap(d => d.slots) ?? [];
  mount(el, html`
    ${mine.slice(0, 2).map(n => html`
      <section class="card card-row">
        <span class="play-thumb" aria-hidden="true"><span class="ti ti-hand"></span></span>
        <span class="card-main">
          <span class="eyebrow">Ton créneau</span>
          <span class="card-title">${n.label} · ${day(n.occurrence_date)}</span>
          <span class="muted small">${n.title} · ${needWhen(n)}${others(n).length ? ` · avec ${others(n).slice(0, 3).join(', ')}` : ''}</span>
        </span>
        <button type="button" class="btn btn-link btn-sm" data-leave="${n.slot_id}">Me désister</button>
      </section>`)}
    ${firstOpen ? html`
      <section class="card vol-card">
        <span class="eyebrow eyebrow-accent">Le club a besoin de toi</span>
        <span class="card-title">${firstOpen.first.title}</span>
        <span class="muted small">${occasionWhen(firstOpen)}${firstOpen.first.location ? ` · ${firstOpen.first.location}` : ''}</span>
        <span class="occ-chips">${slots.slice(0, 4).map(n => html`<span class="chip chip-mute">${n.label} ${n.taken}/${n.needed}</span>`)}${slots.length > 4 ? html`<span class="chip chip-soft">+${slots.length - 4}</span>` : ''}</span>
        <a class="btn btn-primary btn-sm" href="#/benevoles${isAdmin(ctx) ? '/inscription' : ''}">${open.length > 1 ? 'Voir les créneaux' : 'Prendre ce créneau'}</a>
      </section>` : ''}
    <div id="vol-cap"></div>`);
  capEl();

  for (const b of $$<HTMLButtonElement>(el, '[data-leave]')) {
    b.addEventListener('click', async () => {
      try { await leaveSlot(b.dataset.leave!); slotsChanged(); toast('Désistement noté'); await reload(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
}

const isAdmin = (ctx: Ctx) => !!ctx.club && ctx.adminOf.some(c => c.id === ctx.club!.id);

/** Les inscrits d'un créneau, chacun avec la pastille de son groupe (D-105, posée par decoratePins). */
const namesWithPins = (n: VolunteerNeed): Html | string => n.names.length
  ? html`${n.names.map((x, i) => html`${i ? ', ' : ''}<span data-pin-slot="${n.slot_id}" data-pin-i="${i}">${x}</span>`)}` : '—';

/** Bureau (D-119) : chaque prénom inscrit se touche pour le retirer avec un merci (la pastille du groupe y reste). */
const namesToThank = (n: VolunteerNeed): Html | string => n.names.length
  ? html`${n.names.map((x, i) => html`${i ? ', ' : ''}<button type="button" class="bm-who" data-who="${n.slot_id}" data-who-i="${i}" data-pin-slot="${n.slot_id}" data-pin-i="${i}"
      aria-label="${x} : retirer avec un merci">${x}</button>`)}` : '—';

/** Une occasion et ses créneaux, jour par jour. `act` : le bouton d'un créneau (membre) ou rien (bureau) ;
 *  `note` : une ligne sous le nom du créneau (ce qui m'occupe à son heure, D-103). */
function occasionCard(o: Occasion<VolunteerNeed>, desc: string | null, act: (n: VolunteerNeed) => Html | string, tools: Html | string = '',
  note: (n: VolunteerNeed) => Html | string = () => '', who: (n: VolunteerNeed) => Html | string = namesWithPins): Html {
  const f = o.first;
  const left = o.needed - o.taken;
  const pct = o.needed ? Math.round((o.taken / o.needed) * 100) : 100;
  const multi = o.days.length > 1;
  return html`
    <section class="block vol-occ">
      <div class="vol-occ-head">
        <span class="chip ${o.source === 'call' ? 'chip-accent' : 'chip-mute'}">${SOURCE_LABEL[o.source]}</span>
        <span class="chip ${left ? 'chip-warn' : 'chip-ok'}">${leftLabel(left)}</span>
      </div>
      <h3>${f.title}</h3>
      <p class="muted small">${occasionWhen(o)}${f.location ? ` · ${f.location}` : ''}</p>
      ${desc ? html`<p class="vol-desc">${desc}</p>` : ''}
      <span class="kpi-bar" role="img" aria-label="${o.taken} sur ${o.needed}"><span style="width:${pct}%"></span></span>
      ${o.days.map(d => html`
        ${multi ? html`<p class="vol-day">${cap(day(d.date))}</p>` : ''}
        <ul class="vol-slots">${d.slots.map(n => html`
          <li class="${n.taken >= n.needed ? 'full' : ''}">
            <span class="vol-what"><strong>${n.label}</strong>${n.source === 'call' ? html`<small>${needWhen(n)}</small>` : ''}</span>
            <span class="muted small">${who(n)} · ${n.taken}/${n.needed}</span>
            ${act(n)}${note(n)}
          </li>`)}</ul>`)}
      ${tools}
    </section>`;
}

/** « Mes créneaux » (D-103) : la date, le poste, avec qui, « Me désister ». En tête de la page Bénévolat. */
function myBlock(ctx: Ctx, mine: VolunteerNeed[]): Html {
  const me = knownName(ctx);
  const others = (n: VolunteerNeed) => { const i = n.names.indexOf(me); return i < 0 ? n.names.slice(1) : n.names.filter((_, j) => j !== i); };
  const dd = (d: string) => parseLocalDate(d);
  return html`
    <section class="block vol-mine" id="mes-creneaux" data-guide="vol-mine">
      <div class="block-head"><h3>Mes créneaux</h3>${mine.length ? html`<span class="chip chip-ok">${mine.length}</span>` : ''}</div>
      ${mine.length ? html`<ul class="vol-mine-list">${mine.map(n => html`
        <li>
          <span class="ce-tile" aria-hidden="true"><b>${dd(n.occurrence_date).getDate()}</b><small>${dd(n.occurrence_date).toLocaleDateString('fr-FR', { month: 'short' }).replace('.', '')}</small></span>
          <span class="vol-mine-main"><strong>${n.label} · ${n.title}</strong>
            <span class="muted small">${cap(day(n.occurrence_date))} · ${needWhen(n)}${n.location ? ` · ${n.location}` : ''}</span>
            <span class="small">${others(n).length ? `Avec ${others(n).join(', ')}` : 'Personne d’autre pour l’instant'}</span></span>
          <button type="button" class="btn btn-link btn-sm" data-leave="${n.slot_id}">Me désister</button>
        </li>`)}</ul>`
      : html`<p class="muted small">${tv(ctx, 'Aucun créneau pour l’instant : choisis-en un ci-dessous.', 'Aucun créneau pour l’instant : choisissez-en un ci-dessous.')}</p>`}
    </section>`;
}

/** Page « Bénévolat » des membres (lien des annonces de relance, « Mes créneaux » de l'accueil) : mes créneaux,
 *  puis toutes les occasions à venir ; un créneau en conflit avec ce qui m'occupe le dit (D-103). */
export async function renderVolunteerPage(root: HTMLElement, ctx: Ctx): Promise<void> {
  if (!ctx.club) { mount(root, html`<div class="empty"><p class="empty-title">Aucun club.</p></div>`); return; }
  const club = ctx.club;
  const [{ needs, groups, reserved, conflicts }, calls] = await Promise.all([
    slotsForMe(ctx, PAGE_DAYS),
    volunteerCalls(club.id).catch(() => [] as VolunteerCall[]),
  ]);
  const desc = new Map(calls.map(c => [c.id, c.description]));
  // Un créneau confié à un groupe (D-065) : réservé à ses membres.
  const lockedBy = (n: VolunteerNeed) => { const g = groups.find(x => x.id === reserved.get(n.slot_id)); return g && !g.is_member ? g : null; };
  const known = knownName(ctx);
  const reload = () => renderVolunteerPage(root, ctx);
  const list = occasions(needs);
  const mine = needs.filter(n => n.mine);
  const minor = isMinorAccount(ctx);   // D-103 : les créneaux ouverts aux jeunes (la base ne donne qu'eux), pas de groupe
  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Bénévolat</h2>
        <p class="muted">${mine.length ? 'Merci pour le club !' : 'Un créneau, un geste. Merci pour le club !'}</p></header>
      ${myBlock(ctx, mine)}
      <div id="vol-hero"></div>
      ${minor ? html`<p class="hint">Les créneaux que le club ouvre aux jeunes. Tes parents sont prévenus quand tu en prends un.</p>` : html`<div id="vol-groups" class="screen"></div>`}
      ${isAdmin(ctx) ? html`<a class="btn-link-accent" href="#/benevoles">← Gérer les bénévoles</a>` : minor ? '' : html`<a class="btn-link-accent" href="#/benevoles/groupes">Les groupes de bénévoles du club →</a>`}
      ${known ? '' : html`<label class="field"><span>${tv(ctx, 'Ton prénom (vu par le club)', 'Votre prénom (vu par le club)')}</span><input id="v-name" maxlength="40" placeholder="ex. Claire" autocomplete="given-name"></label>`}
      ${list.length ? html`<h3 class="sec-title vol-all">Les créneaux du club</h3>${list.map(o => occasionCard(o, o.source === 'call' ? desc.get(o.first.event_id) ?? null : null, n =>
          n.mine ? html`<button type="button" class="btn btn-link btn-sm" data-leave="${n.slot_id}">Me désister</button>`
          : lockedBy(n) ? html`<span class="chip chip-mute">Réservé · ${lockedBy(n)!.emoji} ${lockedBy(n)!.name}</span>`
          : n.taken < n.needed ? html`<button type="button" class="btn ${conflicts.has(n.slot_id) ? 'btn-ghost' : 'btn-primary'} btn-sm" data-guide="vol-take" data-take="${n.slot_id}">Je prends</button>` : html`<b>✓</b>`,
          '', n => (n.mine || n.taken >= n.needed ? '' : conflictNote(ctx, conflicts.get(n.slot_id)))))}`
        : emptyGuide({ art: 'ti-heart', guide: 'vol-empty', title: 'Aucun créneau à prendre',
            text: minor ? 'Les créneaux que le club ouvre aux jeunes (table de marque, chrono, arbitrage des petits) apparaîtront ici. Chaque créneau tenu compte une mission dans tes badges.'
              : tv(ctx, 'Les créneaux des matchs à domicile, des événements et des appels du club apparaîtront ici. Chaque créneau tenu compte une mission dans tes badges.',
                'Les créneaux des matchs à domicile, des événements et des appels du club apparaîtront ici. Chaque créneau tenu compte une mission dans vos badges.') })}
    </section>`);
  void renderBadgesHome($(root, '#vol-hero')!, ctx, { cap: false, strip: true });   // la bande « Mes badges » (D-100)
  void decoratePins(root, club.id);   // la pastille du groupe à côté des prénoms (D-105)
  if (!minor) void renderMyGroupsCard($(root, '#vol-groups')!, ctx);
  for (const b of $$<HTMLButtonElement>(root, '[data-take]')) {
    b.addEventListener('click', async () => {
      const name = known || ($<HTMLInputElement>(root, '#v-name')?.value.trim() ?? '');
      if (!name) { toast(tv(ctx, 'Indique ton prénom.', 'Indiquez votre prénom.'), 'ko'); $<HTMLInputElement>(root, '#v-name')?.focus(); return; }
      if (!(await confirmTake(ctx, needs.find(n => n.slot_id === b.dataset.take), conflicts.get(b.dataset.take!)))) return;
      b.disabled = true;
      try { await takeSlot(b.dataset.take!, name); slotsChanged(); if (!firstName(ctx)) saveName(name); toast('Merci ! Créneau noté'); await reload(); }
      catch (e) {
        const m = String((e as Error).message);
        toast(m.includes('complet') ? 'Ce créneau vient d’être pris.' : m.includes('fermé') ? 'Cet appel est fermé.' : m.includes('passé') ? 'Ce créneau est passé.' : 'Inscription impossible.', 'ko');
        await reload();
      }
    });
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-leave]')) {
    b.addEventListener('click', async () => {
      try { await leaveSlot(b.dataset.leave!); slotsChanged(); toast('Désistement noté'); await reload(); }
      catch { toast('Impossible pour le moment.', 'ko'); }
    });
  }
}

/** Les créneaux d'une occasion, en une ligne : « buvette 14h00 – 18h00 (2), rangement ». */
const slotLine = (slots: VolunteerNeed[], count: (n: VolunteerNeed) => number) =>
  slots.map(n => `${n.label.toLowerCase()}${n.source === 'call' && n.starts_at ? ` ${needWhen(n)}` : ''}${count(n) > 1 ? ` (${count(n)})` : ''}`).join(', ');

/** Annonce de relance, rédigée à partir des créneaux vides (D-028). `about` : une seule occasion. */
export function relanceDraft(needs: VolunteerNeed[], about?: string): Draft {
  const open = needs.filter(n => n.taken < n.needed);
  const missing = holes(open);
  const lines = occasions(open).flatMap(o => o.days.map(d => {
    const at = o.source !== 'call' && o.first.starts_at ? ` à ${hour(o.first.starts_at)}` : '';
    return `• ${cap(day(d.date))}, ${o.first.title}${at} : ${slotLine(d.slots, n => n.needed - n.taken)}`;
  }));
  return {
    team: null,
    title: `${placesLabel(missing)} à prendre ${about ? `· ${about}` : 'pour nos prochains rendez-vous'}`,
    body: [...lines, '', 'Un geste suffit : ouvre l’annonce et choisis ton créneau. Merci pour le club !'].join('\n'),
    link: '#/benevoles',
  };
}

/** Annonce d'un nouvel appel : ce qu'il faut, jour par jour. */
function callDraft(title: string, description: string, needs: VolunteerNeed[]): Draft {
  const lines = occasions(needs).flatMap(o => o.days.map(d => `• ${cap(day(d.date))} : ${slotLine(d.slots, n => n.needed)}`));
  return {
    team: null,
    title: `On a besoin de vous : ${title}`,
    body: [...(description ? [description, ''] : []), ...lines, '', 'Choisis ton créneau en un geste depuis l’annonce. Merci pour le club !'].join('\n'),
    link: '#/benevoles',
  };
}

// -----------------------------------------------------------------------------
// Onglet Bénévoles du bureau
// -----------------------------------------------------------------------------

/** Onglet « Bénévoles » du bureau : demander des bénévoles, suivre qui vient, relancer. */
export async function renderVolunteerBoard(root: HTMLElement, ctx: Ctx, part: 'creneaux' | 'equipe' | 'badges' = 'creneaux'): Promise<void> {
  const club = ctx.club;
  if (!club || !isAdmin(ctx)) return renderVolunteerPage(root, ctx);
  const today = localDate(new Date());
  const [needs, calls, groups, reserved, youth] = await Promise.all([
    clubVolunteerNeeds(club.id, today, addDays(today, PAGE_DAYS)),
    volunteerCalls(club.id).catch(() => [] as VolunteerCall[]),
    clubVolunteerGroups(club.id).catch(() => [] as VolunteerGroup[]),
    slotGroups(club.id).catch(() => new Map<string, string>()),
    slotYouth(club.id, today).catch(() => new Map<string, boolean>()),
  ]);
  const reload = () => renderVolunteerBoard(root, ctx, part);
  const list = occasions(needs);
  const missing = holes(needs);
  // Un seul chiffre (D-103) : les places encore libres des 7 prochains jours ; plus loin, à préparer.
  const week = holes(needs.filter(n => n.occurrence_date <= addDays(today, 7)));
  const later = missing - week;
  const signed = needs.reduce((a, n) => a + n.taken, 0);
  const callById = new Map(calls.map(c => [c.id, c]));
  const closed = calls.filter(c => c.closed_at && c.volunteer_slots.some(s => s.occurrence_date >= today));

  const tools = (o: Occasion<VolunteerNeed>): Html => html`
    <div class="btn-row vol-tools">
      ${o.needed > o.taken ? html`<button type="button" class="btn btn-primary btn-sm" data-relance="${o.key}">Relancer</button>` : ''}
      ${o.source === 'call' ? html`
        <button type="button" class="btn btn-ghost btn-sm" data-edit="${o.first.event_id}">Modifier</button>
        <button type="button" class="btn btn-ghost btn-sm" data-shut="${o.first.event_id}">Fermer l’appel</button>`
      : o.source === 'event' ? html`<a class="btn btn-ghost btn-sm" href="#/agenda">Gérer l’événement</a>`
      : html`<button type="button" class="btn btn-ghost btn-sm" data-needs="${o.first.event_id}" data-guide="vol-match-needs">Modifier les besoins</button>`}
    </div>`;

  mount(root, html`
    <section class="screen">
      <header class="screen-head"><p class="eyebrow">${club.name}</p><h2>Bénévolat</h2>
        ${part === 'creneaux' ? html`<p class="muted">Demande des bénévoles pour n’importe quelle occasion : chacun choisit son créneau en un geste, tu vois qui vient.</p>` : ''}</header>
      <nav class="seg" aria-label="Bénévolat">
        <a href="#/benevoles" ${part === 'creneaux' ? html`aria-current="page"` : ''}>Créneaux</a>
        <a href="#/benevoles/equipe" ${part === 'equipe' ? html`aria-current="page"` : ''}>Groupes</a>
        <a href="#/benevoles/badges" data-guide="vol-badges" ${part === 'badges' ? html`aria-current="page"` : ''}>Badges</a>
      </nav>
      <div class="screen" ${part === 'creneaux' ? '' : 'hidden'}>
      <button type="button" class="btn btn-primary vol-ask" id="b-ask" data-guide="vol-ask"><span aria-hidden="true">＋</span> Demander des bénévoles</button>
      <div class="kpi-grid">
        <div class="kpi"><span class="eyebrow">Places à prendre</span><b>${week}</b><small>${week ? 'dans les 7 prochains jours' : 'Tout est couvert cette semaine'}</small></div>
        <div class="kpi"><span class="eyebrow">Inscrits</span><b>${signed}</b><small>sur les 4 prochains mois${later ? ` · ${placesLabel(later)} à prendre ensuite` : ''}</small></div>
      </div>
      ${missing ? html`<button type="button" class="btn btn-ghost btn-sm vol-relance" id="b-relance-all">Relancer tout le club</button>` : ''}
      ${list.length ? list.map(o => occasionCard(o, o.source === 'call' ? callById.get(o.first.event_id)?.description ?? null : null,
          n => html`<span class="vol-acts">${youthButton(n, youth)}${slotGroupButton(n, groups, reserved)}${n.taken < n.needed
            ? html`<button type="button" class="link-x bm-enrol" data-enrol="${n.slot_id}" data-guide="vol-inscrire">Inscrire quelqu’un</button>` : ''}</span>`,
          tools(o), () => '', namesToThank))
        : emptyGuide({ art: 'ti-heart', guide: 'vol-empty', title: 'Tes bénévoles, sans relancer à la main', tour: 'demander-benevoles',
            text: 'Un appel avec ses créneaux, comme un sondage de dates : chacun prend sa place. Les besoins des matchs à domicile se créent tout seuls.' })}
      ${closed.length ? html`
        <section class="block">
          <h3>Appels fermés</h3>
          <ul class="ce-rows">${closed.map(c => html`
            <li><span><strong>${c.title}</strong><br><span class="muted small">${c.volunteer_slots.length} ${c.volunteer_slots.length > 1 ? 'créneaux' : 'créneau'}, plus d’inscriptions</span></span>
              <button type="button" class="btn btn-ghost btn-sm" data-reopen="${c.id}">Rouvrir</button></li>`)}</ul>
        </section>` : ''}
      <a class="btn-link-accent" href="#/benevoles/inscription">Voir la page des membres →</a>
      </div>
      <div class="screen" ${part === 'equipe' ? '' : 'hidden'}><div id="vol-bureau-groups" class="screen"></div></div>
      <div class="screen" id="vol-admin-extras" ${part === 'badges' ? '' : 'hidden'}></div>
    </section>`);

  if (part === 'badges') void renderBadgesBoard($(root, '#vol-admin-extras')!, ctx);
  if (part === 'creneaux') void decoratePins(root, club.id);   // la pastille du groupe à côté des prénoms (D-105)
  // D-132 : les reçus fiscaux n'ont plus qu'une entrée côté bureau (Club › Financer le club) ; les kilomètres des
  // conducteurs sont sur l'écran des reçus fiscaux.
  if (part === 'equipe') void renderBureauVolunteerSections($(root, '#vol-bureau-groups')!, ctx);
  wireSlotGroups(root, groups, reserved, () => void reload());
  // D-119 : « Modifier les besoins » d'un match, « Inscrire quelqu'un », un prénom à retirer avec un merci.
  wireNeedsTools(root, ctx, needs, () => { slotsChanged(); void reload(); });
  // Ouvert aux jeunes (D-103) : un geste ouvre ou referme le créneau aux comptes mineurs.
  for (const b of $$<HTMLButtonElement>(root, '[data-youth]')) b.addEventListener('click', async () => {
    const open = b.getAttribute('aria-pressed') !== 'true';
    b.disabled = true;
    try { await setSlotYouth(b.dataset.youth!, open); toast(open ? 'Ouvert aux jeunes : les mineurs du club le voient' : 'Réservé aux adultes'); await reload(); }
    catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
  });
  $(root, '#b-ask')!.addEventListener('click', () => openCallEditor(ctx, null, [], reload));
  $(root, '#b-relance-all')?.addEventListener('click', () => openCompose(ctx, () => undefined, relanceDraft(needs)));
  for (const b of $$<HTMLButtonElement>(root, '[data-relance]')) {
    const o = list.find(x => x.key === b.dataset.relance);
    if (o) b.addEventListener('click', () => openCompose(ctx, () => undefined, relanceDraft(o.days.flatMap(d => d.slots), o.first.title)));
  }
  for (const b of $$<HTMLButtonElement>(root, '[data-edit]')) {
    b.addEventListener('click', () => {
      const c = callById.get(b.dataset.edit!);
      if (c) openCallEditor(ctx, c, needs.filter(n => n.source === 'call' && n.event_id === c.id), reload, youth);
    });
  }
  const flip = (attr: string, closedNow: boolean, done: string) => {
    for (const b of $$<HTMLButtonElement>(root, `[${attr}]`)) {
      b.addEventListener('click', async () => {
        b.disabled = true;
        try { await setVolunteerCallClosed(b.getAttribute(attr)!, closedNow); toast(done); await reload(); }
        catch { b.disabled = false; toast('Impossible pour le moment.', 'ko'); }
      });
    }
  };
  flip('data-shut', true, 'Appel fermé : plus d’inscriptions');
  flip('data-reopen', false, 'Appel rouvert');
}

/** Bureau : « Jeunes ✓ » (ouvert aux comptes mineurs) ou « Ouvrir aux jeunes » (D-103). */
function youthButton(n: VolunteerNeed, youth: Map<string, boolean>): Html {
  const on = youth.get(n.slot_id) === true;
  return html`<button type="button" class="chip ${on ? 'chip-ok' : 'chip-mute'} vol-youth" data-youth="${n.slot_id}" aria-pressed="${String(on)}"
    aria-label="${on ? `${n.label} : ouvert aux jeunes, refermer` : `Ouvrir ${n.label} aux jeunes`}">${on ? 'Jeunes ✓' : 'Ouvrir aux jeunes'}</button>`;
}

const LABELS = ['Accueil', 'Buvette', 'Table de marque', 'Chronomètre', 'Installation', 'Rangement', 'Arbitrage', 'Gâteaux', 'Transport', 'Lavage des maillots', 'Photos'];

/** Fiche « Demander des bénévoles » : un titre, des créneaux (date, heures, besoin, nombre). */
function openCallEditor(ctx: Ctx, call: VolunteerCall | null, needs: VolunteerNeed[], onSaved: () => void, youth = new Map<string, boolean>()): void {
  const club = ctx.club!;
  const today = localDate(new Date());
  const taken = new Map(needs.map(n => [n.slot_id, n.taken]));
  let slots: CallSlotDraft[] = call
    ? [...call.volunteer_slots].sort((a, b) => a.position - b.position).map(s => ({
        id: s.id, date: s.occurrence_date, start: toHHMM(s.starts_at), end: toHHMM(s.ends_at), label: s.label, needed: s.needed,
        youth: youth.get(s.id) === true,
      }))
    : [nextSlot(undefined, addDays(today, 7))];

  const row = (s: CallSlotDraft, i: number) => {
    const t = s.id ? taken.get(s.id) ?? 0 : 0;
    return html`
      <li class="call-slot">
        <div class="call-slot-when">
          <input type="date" name="date" value="${s.date}" aria-label="Date du créneau ${i + 1}">
          <input type="time" name="start" value="${s.start}" aria-label="Début du créneau ${i + 1}">
          <span aria-hidden="true">→</span>
          <input type="time" name="end" value="${s.end}" aria-label="Fin du créneau ${i + 1}">
        </div>
        <div class="call-slot-what">
          <input name="label" value="${s.label}" maxlength="60" list="vol-labels" placeholder="Besoin (ex. buvette)" aria-label="Besoin du créneau ${i + 1}">
          <input type="number" name="needed" value="${s.needed}" min="1" max="20" inputmode="numeric" class="ce-num" aria-label="Nombre de personnes, créneau ${i + 1}">
          ${t ? html`<span class="chip chip-ok" title="Déjà ${t} ${t > 1 ? 'inscrits' : 'inscrit'} : le créneau reste">${t} ✓</span>`
            : html`<button type="button" class="sheet-x" data-del="${i}" aria-label="Retirer le créneau ${i + 1}">×</button>`}
        </div>
        <label class="call-youth"><input type="checkbox" name="youth" ${s.youth ? 'checked' : ''}> Ouvert aux jeunes</label>
      </li>`;
  };

  openSheet(call ? 'Modifier l’appel' : 'Demander des bénévoles', html`
    <form id="f-call" class="call-form" novalidate>
      <label class="field"><span>Pour quoi ?</span><input name="title" maxlength="120" value="${call?.title ?? ''}" placeholder="ex. Plateau U9, lavage des maillots"></label>
      <label class="field"><span>Lieu (facultatif)</span><input name="location" maxlength="160" value="${call?.location ?? ''}" placeholder="ex. Gymnase Rabelais"></label>
      <label class="field"><span>Un mot pour les membres (facultatif)</span><textarea name="description" rows="2" maxlength="1000" placeholder="ex. On compte sur vous pour accueillir les petits !">${call?.description ?? ''}</textarea></label>
      <div class="field"><span>Créneaux</span>
        <ul class="call-slots" id="call-slots"></ul>
        <div class="btn-row">
          <button type="button" class="btn btn-ghost btn-sm" id="b-slot">＋ Un créneau</button>
          <button type="button" class="btn btn-ghost btn-sm" id="b-day">＋ Les mêmes le lendemain</button>
        </div>
        <p class="hint">Sans heure : « dans la journée ». Chacun s’inscrit sur un créneau, dans la limite des places. « Ouvert aux jeunes » : les mineurs du club peuvent le prendre.</p>
      </div>
      <datalist id="vol-labels">${LABELS.map(l => html`<option value="${l}"></option>`)}</datalist>
      <p class="form-msg" id="call-msg" role="alert"></p>
      <button class="btn btn-primary" type="submit">${call ? 'Enregistrer' : 'Publier l’appel'}</button>
      ${call ? html`<button type="button" class="btn btn-link btn-sm" id="b-delcall">Supprimer l’appel</button>` : ''}
    </form>`, (sheet, close) => {
    const ul = $(sheet, '#call-slots')!;
    const msg = (t: string) => { $(sheet, '#call-msg')!.textContent = t; };
    // Lit la saisie avant de redessiner (ajout, retrait d'un créneau).
    const read = () => {
      slots = $$<HTMLLIElement>(ul, '.call-slot').map((li, i) => {
        const v = (n: string) => ($<HTMLInputElement>(li, `[name="${n}"]`)?.value ?? '').trim();
        return { id: slots[i]?.id, date: v('date'), start: v('start'), end: v('end'), label: v('label'), needed: Number(v('needed')),
          youth: !!$<HTMLInputElement>(li, '[name="youth"]')?.checked };
      });
    };
    const draw = () => {
      mount(ul, html`${slots.map(row)}`);
      for (const b of $$<HTMLButtonElement>(ul, '[data-del]')) {
        b.addEventListener('click', () => { read(); slots.splice(Number(b.dataset.del), 1); draw(); });
      }
    };
    draw();
    $(sheet, '#b-slot')!.addEventListener('click', () => {
      read(); slots.push(nextSlot(slots[slots.length - 1], addDays(today, 7))); draw();
      $$<HTMLInputElement>(ul, '[name="label"]').pop()?.focus();
    });
    $(sheet, '#b-day')!.addEventListener('click', () => {
      read();
      const more = nextDay(slots, addDays);
      if (!more.length) { msg('Ajoute d’abord un créneau daté.'); return; }
      slots.push(...more); draw();
    });
    const form = $<HTMLFormElement>(sheet, '#f-call')!;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      read();
      const d = new FormData(form);
      const title = String(d.get('title') ?? '').trim();
      const description = String(d.get('description') ?? '').trim();
      const location = String(d.get('location') ?? '').trim();
      const problem = callProblem(title, slots);
      if (problem) { msg(problem); return; }
      const btn = form.querySelector<HTMLButtonElement>('button[type="submit"]')!;
      btn.disabled = true;
      try {
        const id = await saveVolunteerCall(club.id, call?.id ?? null, { title, description, location, slots });
        await setCallYouth(id, slots.map(x => !!x.youth)).catch(() => toast('« Ouvert aux jeunes » non enregistré : réessaie.', 'ko'));
        close();
        onSaved();
        if (call) { toast('Appel enregistré'); return; }
        // Nouvel appel : l'annonce aux membres est prête, il n'y a plus qu'à l'envoyer.
        const fresh = await clubVolunteerNeeds(club.id, today, addDays(today, 366)).catch(() => [] as VolunteerNeed[]);
        openCompose(ctx, () => undefined, callDraft(title, description, fresh.filter(n => n.source === 'call' && n.event_id === id)));
      } catch { btn.disabled = false; msg('Enregistrement impossible. Réessaie.'); }
    });
    const del = $<HTMLButtonElement>(sheet, '#b-delcall');
    del?.addEventListener('click', async () => {
      // Confirmation dans la fiche : un second appui supprime.
      if (!del.dataset.sure) {
        const n = [...taken.values()].reduce((a, x) => a + x, 0);
        del.dataset.sure = '1';
        del.textContent = n ? `Supprimer avec ${n > 1 ? `ses ${n} inscriptions` : 'son inscription'} ? Appuie encore` : 'Appuie encore pour supprimer';
        return;
      }
      try { await deleteVolunteerCall(call!.id); close(); toast('Appel supprimé'); onSaved(); }
      catch { msg('Suppression impossible.'); }
    });
  });
}
